import React from 'react';
import './OrganizationCard.css';

interface Organization {
  id: string;
  name: string;
  type: 'sub-organization' | 'partner' | 'schools' | 'companies';
  description: string;
  members_count: number;
  location: string;
  website?: string;
  logo?: string;
  status: 'active' | 'pending' | 'inactive';
  joinedDate: string;
  contactPerson: string;
  email: string;
  partnership_kind?: string | null;
}

interface OrganizationCardProps {
  organization: Organization & { isParent?: boolean };
  onEdit: () => void;
  onDelete: () => void;
  onAttach?: () => void;
  onPartnership?: () => void;
  onJoin?: () => void;
  onLeave?: () => void;
  onTeacherPartnershipRequest?: () => void;
  isPersonalUser?: boolean;
  onClick?: () => void;
  hideJoinButton?: boolean;
  hideMembersCount?: boolean;
}

const OrganizationCard: React.FC<OrganizationCardProps> = ({ organization, onEdit, onDelete, onAttach, onPartnership, onJoin, onLeave, onTeacherPartnershipRequest, isPersonalUser = false, onClick, hideJoinButton = false, hideMembersCount = false }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'inactive': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'pending': return 'En attente';
      case 'inactive': return 'Inactif';
      default: return 'Inconnu';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'sub-organization': return 'Sous-organisation';
      case 'partner': return 'Partenaire';
      case 'schools': return 'Établissement scolaire';
      case 'companies': return 'Organisation';
      default: return 'Organisation';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'schools': return '#10b981'; // Green for schools
      case 'companies': return '#3b82f6'; // Blue for companies
      default: return '#3b82f6'; // Default to blue
    }
  };

  const getPartnershipKindLabel = (kind: string | null | undefined): string => {
    if (!kind) return '';
    if (kind === 'administratif') return 'Partenariat administratif';
    return kind;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Check if there are hover actions - if so, disable onClick on the card
  const hasHoverActions = onAttach || onPartnership || (onJoin && !hideJoinButton) || onLeave || onTeacherPartnershipRequest;

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // If there are hover actions, prevent the modal from opening
    if (hasHoverActions) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    // Otherwise, call the onClick handler if it exists
    if (onClick) {
      onClick();
    }
  };

  return (
    <div 
      className={`organization-card ${hasHoverActions ? 'has-hover-actions' : ''}`}
      onClick={hasHoverActions ? undefined : handleCardClick}
      style={{ cursor: hasHoverActions ? 'default' : (onClick ? 'pointer' : 'default') }}
    >
      <div className="organization-header">
        <div className="organization-logo">
          {organization.logo ? (
            <img src={organization.logo} alt={organization.name} />
          ) : (
            <div className="logo-placeholder">
              <i className="fas fa-building"></i>
            </div>
          )}
        </div>
        <div className="organization-info">
          <h3 className="organization-name">{organization.name}</h3>
          <div className="organization-meta">
            <span 
              className="organization-type"
              style={{
                background: `${getTypeColor(organization.type)}15`,
                color: getTypeColor(organization.type)
              }}
            >
              {organization.isParent ? 'Parent' : getTypeLabel(organization.type)}
            </span>
            <span 
              className="whitespace-nowrap organization-status" 
              style={{ color: getStatusColor(organization.status) }}
            >
              {getStatusLabel(organization.status)}
            </span>
          </div>
        </div>
        {/* {
          organization.type !== 'schools' && (
            <div className="organization-actions">
              <button 
                className="btn-icon" 
                title="Modifier"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <i className="fas fa-edit"></i>
              </button>
              <button 
                className="btn-icon" 
                title="Supprimer"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          )
        } */}
      </div>

      <div className="organization-content">
        {organization.description && (
          <p className="organization-description">{organization.description}</p>
        )}
        
        <div className="organization-details">
          <div className="detail-item">
            <i className="fas fa-map-marker-alt"></i>
            <span>{organization.location}</span>
          </div>
          {!hideMembersCount && (
            <div className="detail-item">
              <i className="fas fa-users"></i>
              <span>{organization.members_count} membres</span>
            </div>
          )}
          {organization.joinedDate && organization.status === 'active' && (
          <div className="detail-item">
            <i className="fas fa-calendar"></i>
            <span>Rejoint le {formatDate(organization.joinedDate)}</span>
          </div>)}
          {organization.type === 'partner' && organization.partnership_kind && (
          <div className="detail-item">
            <i className="fas fa-handshake"></i>
            <span>Type de partenariat : {getPartnershipKindLabel(organization.partnership_kind)}</span>
          </div>
          )}
          {organization.website && (
            <div className="detail-item">
              <i className="fas fa-globe"></i>
              <a 
                href={organization.website} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {organization.website}
              </a>
            </div>
          )}
        </div>
        {(organization.contactPerson || organization.email) && (
        <div className="organization-contact">
            {organization.contactPerson && (
          <div className="contact-person">
            <i className="fas fa-user"></i>
            <span>{organization.contactPerson}</span>
          </div>
            )}
            {organization.email && (
          <div className="contact-email">
            <i className="fas fa-envelope"></i>
            <a 
              href={`mailto:${organization.email}`}
              onClick={(e) => e.stopPropagation()}
            >
              {organization.email}
            </a>
          </div>
            )}
        </div>
        )}
      </div>


      {/* Hover Actions - Se rattacher, Demander un partenariat, Rejoindre, ou Quitter */}
      {(onAttach || onPartnership || (onJoin && !hideJoinButton) || onLeave || onTeacherPartnershipRequest) && (
        <div className="organization-hover-actions">
          {onAttach && (
            <button 
              className="btn-hover-action btn-attach" 
              onClick={(e) => {
                e.stopPropagation();
                onAttach();
              }}
              title="Se rattacher"
            >
              <i className="fas fa-link"></i>
              <span>Se rattacher</span>
            </button>
          )}
          {onJoin && !hideJoinButton && (
            <button 
              className="btn-hover-action btn-partnership" 
              onClick={(e) => {
                e.stopPropagation();
                onJoin();
              }}
              title="Rejoindre l'organisation"
            >
              <i className="fas fa-user-plus"></i>
              <span>Rejoindre</span>
            </button>
          )}
          {onPartnership && (
            <button 
              className="btn-hover-action btn-partnership" 
              onClick={(e) => {
                e.stopPropagation();
                onPartnership();
              }}
              title="Proposer un partenariat"
            >
              <i className="fas fa-handshake"></i>
              <span>Partenariat</span>
            </button>
          )}
          {onLeave && (
            <button
              className="btn-hover-action btn-leave"
              onClick={(e) => {
                e.stopPropagation();
                onLeave();
              }}
              title="Quitter cette organisation"
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Quitter</span>
            </button>
          )}
          {onTeacherPartnershipRequest && (
            <button 
              className="btn-hover-action btn-partnership" 
              onClick={(e) => {
                e.stopPropagation();
                onTeacherPartnershipRequest();
              }}
              title="Soumettre une demande de partenariat"
            >
              <i className="fas fa-paper-plane"></i>
              <span>Soumettre une demande</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationCard;
